/**
 * Content Safety Filter
 * Provides filtering for inappropriate, explicit, profane, and harmful TEXT content
 * Used to protect students from NSFW, violent, triggering, and explicit material
 *
 * CRITICAL DESIGN PRINCIPLES:
 * 1. ✅ ALL symbols, special characters, numbers, math notation, and science symbols are FULLY ALLOWED
 * 2. ✅ Educational context (500+ chars OR science keywords) bypasses most checks to avoid false positives
 * 3. ✅ Common academic words are whitelisted (class, mass, oral, assessment, etc.)
 * 4. ❌ ONLY STANDALONE full curse words are blocked (NOT substrings within legitimate words)
 * 5. ✅ Word boundaries used for English profanity (prevents "assessment" → "ass" false positive)
 * 6. ✅ Security measures ONLY for: SQL injection, XSS attacks, DDoS spam
 * 7. ✅ NO restrictions on: ×, ÷, ±, √, π, ∑, ∫, °, μ, Ω, or ANY special characters
 * 8. ✅ LaTeX/KaTeX formulas are FULLY SUPPORTED: $formula$, $$formula$$, \(...\), \[...\]
 *
 * EXAMPLES OF FULLY ALLOWED CONTENT:
 * - "oral communication" (subject name)
 * - "mass and acceleration" (physics)
 * - "class assignment" (education)
 * - "assessment results" (education)
 * - ANY formulas with special symbols: E = mc², F = ma, ∑, ∫, √, ×, ÷, ±, etc.
 * - ANY math/science notation with ANY symbols and numbers
 * - LaTeX formulas: $x^2 + y^2 = r^2$, $$\int_0^\infty e^{-x^2} dx = \frac{\sqrt{\pi}}{2}$$
 * - Emojis, Unicode, and all special characters
 */

import { logDebug, logWarn, logError } from "@/utils/secureLogger";

/**
 * Check if text contains LaTeX/KaTeX formulas
 * LaTeX formulas are automatically allowed and bypass certain checks
 */
function containsLatexFormulas(text: string): boolean {
  if (!text || typeof text !== "string") {
    return false;
  }

  // Check for common LaTeX delimiters
  const latexPatterns = [
    /\$\$[\s\S]+?\$\$/, // Display math: $$...$$
    /(?<!\\)\$(?!\$)[\s\S]+?(?<!\\)\$/, // Inline math: $...$
    /\\\[[\s\S]+?\\\]/, // Display math: \[...\]
    /\\\([\s\S]+?\\\)/, // Inline math: \(...\)
    /\\frac\{/, // Fractions
    /\\sqrt\{/, // Square roots
    /\\int[\s_^]/, // Integrals
    /\\sum[\s_^]/, // Summations
    /\\lim[\s_]/, // Limits
  ];

  return latexPatterns.some((pattern) => pattern.test(text));
}

// Safety check result interface
export interface SafetyCheckResult {
  isSafe: boolean;
  blocked: boolean;
  severity: "none" | "low" | "medium" | "high" | "critical";
  reasons: string[];
  message?: string;
  reason?: string;
  detectedTerms?: string[];
}

// Comprehensive list of inappropriate terms categorized by type
// IMPORTANT: These are checked with word boundaries (\\b) for English terms
// This means "sex" will NOT match "Sussex", "assessment" will NOT match "ass", etc.
const EXPLICIT_TERMS = [
  // Adult/Sexual content (blocked in standalone usage, allowed in educational phrases)
  // Note: Terms like "sex", "oral", "breast" are allowed when part of educational exceptions
  "porn",
  "pornography",
  "sex", // Allowed in: "sex education", "sexual reproduction", place names like "Sussex"
  "sexy",
  "sexual", // Allowed in: "sexual reproduction", "sexual selection", "sexual dimorphism"
  "xxx",
  "nude",
  "nudity",
  "naked",
  "nsfw",
  "hentai",
  "erotic",
  "explicit",
  "intercourse",
  "masturbat",
  "orgasm",
  "prostitut",
  "escort",
  "suck",
  "blowjob",
  "handjob",
  "horny",
  "aroused",
  "arousal",
  "climax",
  "ejaculat",
  "vagina",
  "penis",
  "breast", // Allowed in educational biology contexts
  "boob",
  "tit",
  "nipple",
  "areola",
  "clitoris",
  "dildo",
  "vibrator",
  "condom",
  "lubricant",
  "anal",
  "oral sex",
  "threesome",
  "foursome",
  "orgy",
  "gangbang",
  "incest",
  "pedophil",
  "molest",
  "grope",
  "fondle",
  "seduce",
  "seduction",
  "flirt",
  "foreplay",
  "kinky",
  "fetish",
  "bdsm",
  "bondage",
  "dominat",
  "submissive",
  "sadomasochis",
  "voyeur",
  "exhibitionist",
  "strip",
  "stripper",
  "lapdance",
  "webcam",
  "camgirl",
  "onlyfans",
  "sugar daddy",
  "sugar baby",
  "hookup",
  "one night stand",
  "casual sex",
  "virginity",
  "virgin",
  "hymen",
  "erection",
  "boner",
  "wet dream",
  "precum",
  "cumshot",
  "facial",
  "creampie",
  "squirt",
  "g-spot",
  "prostate",
  "pegging",
  "rimming",
  "fisting",
  "fingering",
  "edging",
  "tantric",
  "kama sutra",
  "lingerie",
  "underwear model",
  "playboy",
  "hustler",
  "pornstar",
  "escort service",
  "brothel",
  "red light",
  "adult film",
  "adult video",
  "adult content",
  "18+",
  "21+",
  "mature content",
  "explicit content",

  // Extreme profanity (ONLY standalone words, NOT substrings to avoid false positives)
  // Note: These MUST appear as complete words, not as parts of legitimate words
  // Examples that should NOT be blocked: "class", "mass", "brass", "pass", "assess", "oral"
  "fuck",
  "fucking",
  "fucked",
  "fucker",
  "fuckers",
  "motherfucker",
  "motherfucking",
  "motherfuckers",
  "fuckface",
  "fuckhead",
  "fuckboy",
  "clusterfuck",
  "shit",
  "shit",
  "shitty",
  "shithead",
  "shitface",
  "bullshit",
  "horseshit",
  "batshit",
  "apeshit",
  "dipshit",
  "jackshit",
  "bitch",
  "bitches",
  "bitchy",
  "bitching",
  "son of a bitch",
  "sonovabitch",
  "bitch ass",
  "bitchass",
  "cunt",
  "cunts",
  "dick",
  "dicks",
  "dickhead",
  "dickface",
  "dickwad",
  "cock",
  "cocks",
  "cocksucker",
  "cocksucking",
  "pussy",
  "pussies",
  // "ass", // REMOVED: Too many false positives (class, mass, pass, brass, glass, etc.)
  "asshole",
  "assholes",
  "arse",
  "arsehole",
  "bastard",
  "bastards",
  "damn",
  "damned",
  "dammit",
  "goddamn",
  "goddammit",
  "hell",
  "whore",
  "whores",
  "slut",
  "sluts",
  "slutty",
  "fag",
  "faggot",
  "faggots",
  "nigga",
  "nigger",
  "niggers",
  "retard",
  "retarded",
  "retards",

  // Filipino profanity and curse words (including common variations and derivatives)
  "putangina",
  "putang ina",
  "potangina",
  "potang ina",
  "pokinangina", // Added: variation
  "pokenangina", // Added: variation
  "tangina",
  "tang ina",
  "puta",
  "pota",
  "gago",
  "gaga",
  "tarantado",
  "tarantada",
  "tanga",
  "bobo",
  "ulol",
  "inutil",
  "leche",
  "yawa",
  "pakyu",
  "pak you",
  "punyeta",
  "puñeta",
  "bwisit",
  "bwiset",
  "hayop",
  "animal ka",
  "peste",
  "hindot",
  "kantot",
  "kantutan", // Added: derivative of kantot
  "kantutan mo", // Added: variation
  "bilat",
  "tamod",
  "tite",
  "titi",
  "betlog",
  "bayag",
  "jakol",
  "jakolero", // Added: derivative
  "salsal",
  "chupa",
  "tsupa",
  "suso",
  "pepe", // Added: vulgar for vagina
  "puki", // Added: vulgar for vagina
  "utong", // Added: nipple (vulgar context)
  "nene", // Added: breast (vulgar context)
  "dede", // Added: breast (vulgar context)
  "gatas", // Added: can be used vulgarly
  "talong", // Added: eggplant (sexual innuendo)
  "saging", // Added: banana (sexual innuendo)
  "burat", // Added: vulgar for penis

  // Filipino violence/harmful terms
  "mamatay",
  "pumatay",
  "patayin",
  "kitlin",
  "bugbog",
  "bugbugin",
  "saksakin",
  "saksak",
  "baril",
  "barilin",
  "bomba",
  "pagsabog",
  "magpakamatay",
  "suicide",

  // Violence/Gore
  "kill",
  "murder",
  "rape",
  "torture",
  "mutilat",
  "dismember",
  "gore",
  "blood",
  "violent",
  "abuse",
  "assault",
  "terror",
  "suicide",
  "self-harm",

  // Drugs/Substances
  "cocaine",
  "heroin",
  "meth",
  "marijuana",
  "cannabis",
  "weed",
  "drug dealer",
  "narcotic",
  "crack",
  "ecstasy",
  "lsd",
  "mdma",

  // Hate speech/Discrimination
  "racist",
  "sexist",
  "homophob",
  "transphob",
  "xenophob",
  "supremac",

  // Weapons (specific harmful contexts)
  "bomb making",
  "explosiv",
  "weapon blueprint",
  "how to kill",

  // Triggering/Harmful content
  "eating disorder",
  "pro-ana",
  "self-mutilat",
  "cutting",
  "suicide method",
];

// Pattern-based detection for harmful instruction patterns ONLY
// ALL special characters, symbols, numbers, and math/science notation are FULLY ALLOWED
// ONLY blocks specific harmful instruction patterns (how to make bombs, how to kill, etc.)
const HARMFUL_PATTERNS = [
  // Filipino violence instruction patterns (VERY SPECIFIC - only "how to" style instructions)
  /\b(paano|how\s+to)\s+(pumatay|mamatay|magpakamatay|kill)\b/gi,
  /\b(gumawa|make|bumuo)\s+(ng)?\s*(bomba|bomb|explosive)\b/gi,

  // Violence instruction patterns (VERY SPECIFIC - only "how to" style instructions)
  /\b(how\s+to\s+)(kill|murder)\s+(someone|people|person)\b/gi,
  /\b(make|build|create)\s+(bomb|explosive|weapon)\b/gi,

  // Self-harm instruction patterns (VERY SPECIFIC - only "how to" style instructions)
  /\b(how\s+to\s+)(commit|attempt)\s+suicide\b/gi,
  /\b(paano|how\s+to)\s+(magpakamatay|suicide|self-harm)\b/gi,

  // Drug production instruction patterns (VERY SPECIFIC - only "how to make" style instructions)
  /\b(how\s+to\s+)(make|cook|produce)\s+(meth|cocaine|drug)\b/gi,
];

// Safe educational terms that might contain fragments but are legitimate
const EDUCATIONAL_EXCEPTIONS = [
  // Common educational words that contain "ass" substring
  "class",
  "classical",
  "assignment",
  "assessment",
  "passage",
  "essay",
  "mass",
  "compass",
  "brass",
  "glass",
  "grass",
  "pass",
  "passed",
  "passing",
  "harass",
  "embarrass",
  "associate",
  "dissociate",
  "assassin",
  "massachusetts",
  "ассess", // Just in case

  // Book/method references
  "dick-read method",
  "moby dick", // legitimate book references

  // Academic/mental health terms
  "assumption",
  "consumption",
  "fundamental",
  "mental health",
  "supplemental",

  // Health/Biology education (legitimate academic topics)
  "sexually transmitted disease",
  "sexual education",
  "sex education", // legitimate health topics
  "sexual reproduction", // biology term
  "sexual selection", // biology/evolution term
  "sexual dimorphism", // biology term
  "asexual", // biology term (asexual reproduction)
  "bisexual", // biology/science term
  "homosexual", // biology/science term
  "heterosexual", // biology/science term
  "intersex", // biology term

  // Communication/Language Arts
  "oral communication", // Subject name
  "oral presentation", // Common academic term
  "oral exam", // Common academic term
  "oral tradition", // Literature/history term
  "oral history", // History term
  "oral reading", // Language arts term
  "oral report", // Common academic term
  "oral defense", // Academic term (thesis defense)

  // Social studies
  "gender",
  "sexual orientation", // legitimate social studies topics
  "unisex", // gender studies term

  // Math terms
  "sexagesimal", // math term (base-60 number system)
  "sextant", // navigation/astronomy tool
  "sextuple", // math term (six times)

  // Common words/suffixes (NO RESTRICTIONS - allow all legitimate words)
  "example",
  "examples",
  "exercise",
  "exercises",
  "expression",
  "expressions",
  "explanation",
  "experiment",
  "experiments",
  "forces example",
  "balances example",
  "processes example",

  // Place names
  "Sussex", // place name
  "Essex", // place name
  "Middlesex", // place name
  "Wessex", // place name

  // Physics and chemistry terms
  "mass",
  "electromagnetic",
  "electromagnetic waves",
  "electromagnetic spectrum",
  "electromagnetic radiation",
  "electromagnetic field",
  "electromagnetic induction",
  "electromagnetism",
  "nuclear",
  "nuclear energy",
  "nuclear reaction",
  "nuclear fission",
  "nuclear fusion",
  "radiation",
  "radioactive",
  "radioactivity",
  "alpha particle",
  "beta particle",
  "gamma ray",
  "x-ray",
  "wavelength",
  "frequency",
  "amplitude",
  "hertz",
  "infrared",
  "ultraviolet",
  "microwave",
  "radio wave",

  // Math and science notation
  "exponent",
  "exponential",
  "expression",
  "equation",
  "inequality",
  "variable",
  "coefficient",
  "constant",
  "function",
  "derivative",
  "integral",
  "limit",
  "theorem",
  "proof",
  "axiom",
  "hypothesis",

  // Chemistry terms
  "compound",
  "compound interest", // also math term
  "element",
  "molecule",
  "atom",
  "ion",
  "electron",
  "proton",
  "neutron",
  "oxidation",
  "reduction",
  "acid",
  "base",
  "salt",
  "catalyst",
  "reaction",
  "chemical reaction",
  "synthesis",
  "decomposition",

  // Biology terms
  "cell",
  "nucleus",
  "mitochondria",
  "chromosome",
  "gene",
  "dna",
  "rna",
  "protein",
  "enzyme",
  "membrane",
  "organelle",
  "tissue",
  "organ",
  "system",
  "organism",
  "species",
  "population",
  "ecosystem",
  "photosynthesis",
  "respiration",
  "metabolism",

  // Earth science terms
  "rock cycle",
  "igneous",
  "sedimentary",
  "metamorphic",
  "mineral",
  "crystal",
  "magma",
  "lava",
  "volcano",
  "earthquake",
  "plate tectonics",
  "weathering",
  "erosion",
  "deposition",
  "atmosphere",
  "hydrosphere",
  "lithosphere",
  "biosphere",

  // Filipino educational exceptions
  "kamatayan", // death (historical/literary context)
  "pagkamatay", // death (historical/literary context)
  "kasaysayan ng kamatayan", // history of death
  "holocaust",
  "martial law",
  "war",
  "digmaan",
  "revolution",
  "rebolusyon",
  "paglaban", // resistance/fight (historical context)
  "pakikipaglaban", // struggle (historical context)
  "kalayaan", // freedom
  "independence",
  "mental health awareness",
  "kalusugan ng isip",
  "suicide prevention", // legitimate health education
  "pagpigil sa pagpapakamatay", // suicide prevention in Filipino
];

/**
 * Normalize fancy Unicode text to standard ASCII for better profanity detection
 * ONLY normalizes text letters (small caps, fullwidth) that could be used to bypass profanity filters
 * FULLY PRESERVES: ALL symbols, math notation (×, ÷, ±, ∑, ∫, √, π), special characters, numbers
 * This function ONLY does letter substitutions for profanity detection - nothing else is modified
 */
function normalizeFancyUnicode(text: string): string {
  // ONLY map text letter substitutions (small caps, fullwidth) that could be used for profanity
  // Does NOT map any math symbols, science notation, or special characters
  const unicodeMap: { [key: string]: string } = {
    // Small caps (commonly used for profanity)
    ᴀ: "a",
    ʙ: "b",
    ᴄ: "c",
    ᴅ: "d",
    ᴇ: "e",
    ғ: "f",
    ɢ: "g",
    ʜ: "h",
    ɪ: "i",
    ᴊ: "j",
    ᴋ: "k",
    ʟ: "l",
    ᴍ: "m",
    ɴ: "n",
    ᴏ: "o",
    ᴘ: "p",
    ʀ: "r",
    ᴛ: "t",
    ᴜ: "u",
    ᴠ: "v",
    ᴡ: "w",
    ʏ: "y",
    ᴢ: "z",
    // Fullwidth (commonly used for profanity)
    Ａ: "A",
    Ｂ: "B",
    Ｃ: "C",
    Ｄ: "D",
    Ｅ: "E",
    Ｆ: "F",
    Ｇ: "G",
    Ｈ: "H",
    Ｉ: "I",
    Ｊ: "J",
    Ｋ: "K",
    Ｌ: "L",
    Ｍ: "M",
    Ｎ: "N",
    Ｏ: "O",
    Ｐ: "P",
    Ｑ: "Q",
    Ｒ: "R",
    Ｓ: "S",
    Ｔ: "T",
    Ｕ: "U",
    Ｖ: "V",
    Ｗ: "W",
    Ｘ: "X",
    Ｙ: "Y",
    Ｚ: "Z",
    ａ: "a",
    ｂ: "b",
    ｃ: "c",
    ｄ: "d",
    ｅ: "e",
    ｆ: "f",
    ｇ: "g",
    ｈ: "h",
    ｉ: "i",
    ｊ: "j",
    ｋ: "k",
    ｌ: "l",
    ｍ: "m",
    ｎ: "n",
    ｏ: "o",
    ｐ: "p",
    ｑ: "q",
    ｒ: "r",
    ｓ: "s",
    ｔ: "t",
    ｕ: "u",
    ｖ: "v",
    ｗ: "w",
    ｘ: "x",
    ｙ: "y",
    ｚ: "z",
  };

  let normalized = text;
  for (const [unicode, ascii] of Object.entries(unicodeMap)) {
    normalized = normalized.replace(new RegExp(unicode, "g"), ascii);
  }

  // Only normalize whitespace - ALL other characters preserved
  normalized = normalized.replace(/\s+/g, " ").trim();

  return normalized;
}

/**
 * Check if text contains inappropriate, explicit, or harmful content
 * Returns SafetyCheckResult with detailed information
 */
export function checkContentSafety(text: string): SafetyCheckResult {
  if (!text || typeof text !== "string") {
    return {
      isSafe: true,
      blocked: false,
      severity: "none",
      reasons: [],
      detectedTerms: [],
    };
  }

  const lowerText = text.toLowerCase();
  const detectedTerms: string[] = [];
  const reasons: string[] = [];

  // Step 0: Normalize fancy Unicode text to catch styled profanity
  const normalizedText = normalizeFancyUnicode(text);
  const normalizedLower = normalizedText.toLowerCase();

  // Step 1: Check against educational exceptions first
  const hasEducationalContext = EDUCATIONAL_EXCEPTIONS.some((term) =>
    lowerText.includes(term.toLowerCase())
  );

  // ENHANCED: Check for strong educational/academic context (longer content = likely educational)
  const hasStrongEducationalIndicators =
    text.length > 500 || // Long content is likely educational notes
    containsLatexFormulas(text) || // ✅ NEW: LaTeX formulas indicate educational/math content
    lowerText.includes("physics") ||
    lowerText.includes("chemistry") ||
    lowerText.includes("biology") ||
    lowerText.includes("mathematics") ||
    lowerText.includes("science") ||
    lowerText.includes("study") ||
    lowerText.includes("equation") ||
    lowerText.includes("formula") ||
    lowerText.includes("theory") ||
    lowerText.includes("law of") ||
    lowerText.includes("chapter") ||
    lowerText.includes("lesson") ||
    lowerText.includes("electromagnetic") ||
    lowerText.includes("thermodynamics") ||
    lowerText.includes("kinematics") ||
    lowerText.includes("mechanics") ||
    lowerText.includes("newton") ||
    lowerText.includes("einstein") ||
    lowerText.includes("experiment") ||
    lowerText.includes("observation") ||
    lowerText.includes("hypothesis") ||
    lowerText.includes("photosynthesis") ||
    lowerText.includes("compound interest") ||
    lowerText.includes("rock cycle");

  logDebug("Content safety check initiated", {
    textLength: text.length,
    normalizedLength: normalizedText.length,
    hasEducationalContext,
    hasStrongEducationalIndicators,
    hasLatexFormulas: containsLatexFormulas(text),
    preview: text.substring(0, 100),
    normalizedPreview: normalizedText.substring(0, 100),
  });

  // Step 2: Check explicit terms on BOTH original and normalized text
  for (const term of EXPLICIT_TERMS) {
    // For Filipino profanity and multi-word terms, use simple includes() for better detection
    // For single English words, use word boundaries
    let isMatch = false;

    if (term.includes(" ")) {
      // Multi-word terms (e.g., "putang ina", "tang ina") - use simple includes
      isMatch =
        lowerText.includes(term.toLowerCase()) || normalizedLower.includes(term.toLowerCase());
    } else {
      // Single-word terms - check both with word boundaries AND simple includes for Filipino profanity
      const pattern = new RegExp(`\\b${term}\\b`, "gi");
      const patternMatch = pattern.test(lowerText) || pattern.test(normalizedLower);

      // Also check simple includes for Filipino profanity (to catch "pokenangina" → "tangina")
      const filipinoProfanity = [
        "putangina",
        "potangina",
        "pokinangina", // Added: variation
        "pokenangina", // Added: variation
        "tangina",
        "puta",
        "pota",
        "gago",
        "gaga",
        "tarantado",
        "tarantada",
        "tanga",
        "bobo",
        "ulol",
        "inutil",
        "leche",
        "yawa",
        "pakyu",
        "punyeta",
        "puñeta", // Spanish ñ version
        "bwisit",
        "bwiset",
        "hayop",
        "peste",
        "hindot",
        "kantot",
        "kantutan", // Added: derivative of kantot
        "bilat",
        "tamod",
        "tite",
        "titi",
        "betlog",
        "bayag",
        "jakol",
        "jakolero", // Added: derivative
        "salsal",
        "chupa",
        "tsupa",
        "suso",
        "pepe", // Added: vulgar for vagina
        "puki", // Added: vulgar for vagina
        "utong", // Added: nipple (vulgar context)
        "nene", // Added: breast (vulgar context)
        "dede", // Added: breast (vulgar context)
        "burat", // Added: vulgar for penis
      ];

      if (filipinoProfanity.includes(term.toLowerCase())) {
        // For Filipino profanity, match anywhere in the text (to catch variations)
        isMatch =
          lowerText.includes(term.toLowerCase()) || normalizedLower.includes(term.toLowerCase());
      } else {
        // For English profanity, use word boundaries
        isMatch = patternMatch;
      }
    }

    if (isMatch) {
      // ENHANCED: Skip if strong educational context exists (prevents false positives in science notes)
      if (hasStrongEducationalIndicators) {
        logDebug(
          `Term "${term}" ignored - strong educational context detected (likely false positive)`
        );
        continue; // Skip to next term without flagging
      }

      // Check if it's part of an educational exception
      let isException = false;

      // Method 1: Check if the exception phrase contains the term (original logic)
      for (const exception of EDUCATIONAL_EXCEPTIONS) {
        if (lowerText.includes(exception.toLowerCase()) && exception.toLowerCase().includes(term)) {
          isException = true;
          logDebug(`Term "${term}" allowed - found in educational exception: "${exception}"`);
          break;
        }
      }

      // Method 2: Check if the term appears in a broader educational context
      // Look for science/math/education keywords near the detected term
      if (!isException) {
        const educationalContextKeywords = [
          "physics",
          "chemistry",
          "biology",
          "science",
          "math",
          "equation",
          "formula",
          "theory",
          "experiment",
          "study",
          "research",
          "analysis",
          "calculate",
          "solve",
          "prove",
          "define",
          "explain",
          "describe",
          "lesson",
          "chapter",
          "unit",
          "topic",
          "subject",
          "course",
          "electromagnetic",
          "radiation",
          "spectrum",
          "wavelength",
          "frequency",
          "compound",
          "element",
          "molecule",
          "atom",
          "reaction",
          "rock cycle",
          "geology",
          "earth science",
          "mineral",
          "crystal",
        ];

        const hasEducationalContext = educationalContextKeywords.some((keyword) =>
          lowerText.includes(keyword.toLowerCase())
        );

        if (hasEducationalContext) {
          isException = true;
          logDebug(`Term "${term}" allowed - found in educational science/math context`);
        }
      }

      if (!isException) {
        detectedTerms.push(term);
        if (!reasons.includes("Explicit or profane language detected")) {
          reasons.push("Explicit or profane language detected");
        }
      }
    }
  }

  // Step 3: Check only for specific harmful instruction patterns (violence/self-harm/drug production)
  // ALL symbols, characters, numbers, and special characters are ALLOWED
  // Only blocks very specific "how to" harmful instruction patterns
  for (const pattern of HARMFUL_PATTERNS) {
    const matches = text.match(pattern) || normalizedText.match(pattern);
    if (matches) {
      detectedTerms.push(...matches.map((m) => m.toLowerCase()));
      if (!reasons.includes("Harmful instruction patterns detected")) {
        reasons.push("Harmful instruction patterns detected");
      }
    }
  }

  // Step 4: Determine severity and if content should be blocked
  let severity: "none" | "low" | "medium" | "high" | "critical" = "none";
  let blocked = false;

  if (detectedTerms.length > 0) {
    // Determine severity based on detected terms
    const hasCriticalTerms = detectedTerms.some((term) =>
      [
        // English profanity (most severe) - including compound words
        "fuck",
        "fucking",
        "fucked",
        "fucker",
        "fuckers",
        "motherfucker",
        "motherfucking",
        "motherfuckers",
        "fuckface",
        "fuckhead",
        "fuckboy",
        "clusterfuck",
        "shit",
        "shitty",
        "shithead",
        "shitface",
        "bullshit",
        "horseshit",
        "batshit",
        "apeshit",
        "dipshit",
        "jackshit",
        "bitch",
        "bitches",
        "bitchy",
        "bitching",
        "son of a bitch",
        "sonovabitch",
        "bitch ass",
        "bitchass",
        "cunt",
        "cunts",
        "dick",
        "dickhead",
        "dickface",
        "dickwad",
        "cock",
        "cocksucker",
        "cocksucking",
        "pussy",
        "pussies",
        "asshole",
        "assholes",
        "arse",
        "arsehole",
        "bastard",
        "bastards",
        "whore",
        "whores",
        "slut",
        "sluts",
        "slutty",
        "fag",
        "faggot",
        "faggots",
        "nigga",
        "nigger",
        "niggers",
        "retard",
        "retarded",
        "retards",
        "porn",
        "sex",
        // Sexual/explicit content (critical - no biology education)
        "suck",
        "nipple",
        "dildo",
        "breast",
        "boob",
        "tit",
        "vagina",
        "penis",
        "blowjob",
        "handjob",
        "anal",
        "oral sex",
        "threesome",
        "orgy",
        "gangbang",
        "incest",
        "pedophil",
        "molest",
        "rape",
        "horny",
        "arousal",
        "climax",
        "ejaculat",
        "erection",
        "masturbat",
        "orgasm",
        "vibrator",
        "condom",
        "pornography",
        "pornstar",
        "stripper",
        "escort",
        "prostitut",
        "brothel",
        "onlyfans",
        "kinky",
        "fetish",
        "bdsm",
        "bondage",
        "dominat",
        "submissive",
        "voyeur",
        // Filipino profanity (ALL are critical severity)
        "putangina",
        "potangina",
        "pokinangina",
        "pokenangina",
        "tangina",
        "puta",
        "pota",
        "gago",
        "gaga",
        "leche",
        "ulol",
        "pakyu",
        "punyeta",
        "puñeta",
        "bwisit",
        "bwiset",
        "tanga",
        "bobo",
        "yawa",
        "hayop",
        "peste",
        "tarantado",
        "tarantada",
        "inutil",
        // Filipino vulgar/sexual terms (critical)
        "hindot",
        "kantot",
        "kantutan",
        "bilat",
        "tamod",
        "tite",
        "titi",
        "betlog",
        "bayag",
        "jakol",
        "jakolero",
        "salsal",
        "chupa",
        "tsupa",
        "suso",
        "pepe",
        "puki",
        "utong",
        "nene",
        "dede",
        "gatas",
        "talong",
        "saging",
        "burat",
      ].includes(term.toLowerCase())
    );

    const hasHighTerms = detectedTerms.some((term) =>
      ["kill", "murder", "suicide", "drug", "violence"].includes(term.toLowerCase())
    );

    if (hasCriticalTerms) {
      severity = "critical";
      blocked = true;
    } else if (hasHighTerms) {
      severity = "high";
      blocked = true;
    } else if (detectedTerms.length >= 3) {
      severity = "medium";
      blocked = true;
    } else {
      severity = "low";
      blocked = false;
    }

    logWarn("⚠️ CONTENT SAFETY VIOLATION DETECTED", {
      detectedTerms: detectedTerms.slice(0, 5), // Log first 5 terms only
      totalViolations: detectedTerms.length,
      severity,
      blocked,
      textPreview: text.substring(0, 50),
    });

    const message = blocked
      ? "🚫 Content blocked: Your input contains inappropriate content that cannot be processed. Please remove explicit language, profanity, violence, or other inappropriate material and try again."
      : "⚠️ Warning: Your input may contain language that could be inappropriate.";

    return {
      isSafe: !blocked,
      blocked,
      severity,
      reasons,
      message,
      reason:
        "Content contains inappropriate, explicit, or harmful material that violates our safety policies.",
      detectedTerms: detectedTerms.slice(0, 5), // Return first 5 terms
    };
  }

  logDebug("✅ Content passed safety check", {
    textLength: text.length,
  });

  return {
    isSafe: true,
    blocked: false,
    severity: "none",
    reasons: [],
    detectedTerms: [],
  };
}

/**
 * Sanitize search query to add SafeSearch parameters and filter inappropriate terms
 */
export function sanitizeSearchQuery(query: string): string {
  // First check if query is safe
  const safetyCheck = checkContentSafety(query);
  if (!safetyCheck.isSafe) {
    logWarn("Unsafe search query blocked", {
      originalQuery: query.substring(0, 50),
      reason: safetyCheck.reason,
    });
    // Return a safe default query instead of the unsafe one
    return "educational content site:.edu";
  }

  // Add SafeSearch parameters to query
  // Note: Google Custom Search API respects these parameters
  let sanitizedQuery = query;

  // Ensure safe search exclusions are present
  const safeSearchExclusions = [
    "-porn",
    "-xxx",
    "-adult",
    "-explicit",
    "-nsfw",
    "-violence",
    "-gore",
    "-disturbing",
  ];

  // Add SafeSearch exclusions if not already present
  safeSearchExclusions.forEach((exclusion) => {
    if (!sanitizedQuery.includes(exclusion)) {
      sanitizedQuery += ` ${exclusion}`;
    }
  });

  logDebug("Search query sanitized with SafeSearch", {
    originalLength: query.length,
    sanitizedLength: sanitizedQuery.length,
    addedExclusions: safeSearchExclusions.join(", "),
  });

  return sanitizedQuery;
}

/**
 * Validate file content for safety before processing
 * Returns SafetyCheckResult with detailed information
 */
export function validateFileContentSafety(content: string, fileName: string): SafetyCheckResult {
  logDebug("Validating file content safety", {
    fileName,
    contentLength: content.length,
  });

  const safetyCheck = checkContentSafety(content);

  if (!safetyCheck.isSafe || safetyCheck.blocked) {
    logError("❌ FILE REJECTED - Safety violation detected", {
      fileName,
      reason: safetyCheck.reason,
      violationCount: safetyCheck.detectedTerms?.length || 0,
    });

    return {
      ...safetyCheck,
      message: `The file "${fileName}" contains inappropriate or harmful content and cannot be processed. Please ensure all uploaded materials are educational and appropriate for a high school learning environment.`,
    };
  }

  logDebug("✅ File content passed safety validation", {
    fileName,
    contentLength: content.length,
  });

  return safetyCheck;
}

/**
 * Real-time text input validation (for textarea input)
 * Returns SafetyCheckResult with detailed information
 */
export function validateTextInputSafety(text: string): SafetyCheckResult {
  const safetyCheck = checkContentSafety(text);

  if (!safetyCheck.isSafe) {
    logWarn("⚠️ Real-time input blocked - Safety violation", {
      textLength: text.length,
      violationCount: safetyCheck.detectedTerms?.length || 0,
    });
  }

  return safetyCheck;
}

/**
 * ✅ SECURITY: Detect SQL injection attempts in user input
 * Prevents malicious SQL queries from being processed
 * ✅ IMPROVED: Excludes legitimate academic content (math formulas, statistics)
 */
export function detectSQLInjection(text: string): {
  isAttack: boolean;
  reason?: string;
  detectedPatterns?: string[];
} {
  if (!text || typeof text !== "string") {
    return { isAttack: false };
  }

  const lowerText = text.toLowerCase();
  const detectedPatterns: string[] = [];

  // ✅ ENHANCED: Check if content is likely academic/educational before flagging (VASTLY EXPANDED)
  const academicIndicators = [
    // Math/Statistics keywords (greatly expanded for comprehensive coverage)
    /\b(probability|statistics|variance|mean|median|mode|deviation|distribution|hypothesis|regression|correlation|formula|equation|theorem|proof|calculus|algebra|geometry|trigonometry|arithmetic|exponential|logarithm|derivative|integral|matrix|vector|polynomial|factorial|permutation|combination|ratio|proportion|fraction|decimal|percentage|angle|radius|diameter|circumference|area|volume|perimeter|pythagorean|sine|cosine|tangent|cotangent|secant|cosecant|radian|degree|quadratic|cubic|linear|parabola|hyperbola|ellipse|circle|triangle|square|rectangle|pentagon|hexagon|octagon|polygon|polyhedron|sphere|cylinder|cone|pyramid|prism|cube|summation|sigma|delta|pi|infinity|limit|asymptote|slope|intercept|gradient|coordinate|axis|origin|domain|range|function|relation|sequence|series|convergent|divergent|binomial|gaussian|normal|poisson|chi|coefficient)\b/gi,

    // Science keywords (Physics, Chemistry, Biology)
    /\b(physics|chemistry|biology|atom|molecule|electron|proton|neutron|element|compound|reaction|energy|force|motion|velocity|acceleration|mass|weight|density|pressure|temperature|heat|light|sound|wave|frequency|amplitude|wavelength|electromagnetic|magnetic|electric|current|voltage|resistance|circuit|conductor|insulator|gravity|friction|momentum|kinetic|potential|thermodynamics|entropy|catalyst|acid|base|solution|solvent|concentration|molarity|periodic|isotope|covalent|ionic|cell|DNA|RNA|protein|enzyme|metabolism|photosynthesis|mitosis|evolution|genetics|chromosome|gene|phenotype|genotype|ecosystem|species)\b/gi,

    // Academic document markers
    /\b(chapter|lesson|module|section|exercise|problem|solution|example|definition|note|activity|assessment|quiz|test|exam|homework|assignment|worksheet|syllabus|objective|instruction)\b/gi,

    // Educational context
    /\b(learning|education|study|student|teacher|class|grade|school|university|college|academic|tutorial|course|subject|topic|concept)\b/gi,

    // Mathematical notation indicators (LaTeX-like or special symbols)
    /[\^_∑∏∫∂∆∇√≠≈≤≥±∓×÷∞∈∉⊂⊃∩∪∧∨¬∀∃αβγδεζηθικλμνξοπρστυφχψω]/,
  ];

  const hasAcademicContext = academicIndicators.some((pattern) => pattern.test(text));

  // ✅ ENHANCED: Whitelist for legitimate academic content patterns (VASTLY EXPANDED)
  const academicWhitelist = [
    // Statistics/Probability notation
    /P\s*\(\s*\w+\s+(or|and)\s+\w+\s*\)/gi, // P(A or B), P(A and B)
    /Pr\s*\(\s*\w+\s+(or|and)\s+\w+\s*\)/gi, // Pr() notation

    // Set theory notation
    /\b[A-Z]\s+(or|and|union|intersect|intersection)\s+[A-Z]\b/gi,

    // Logical operators in math
    /\b[p-z]\s+(or|and|implies|if|then|iff)\s+[p-z]\b/gi,

    // Equations with "or"
    /\b\w+\s*[=≠≈<>≤≥]\s*[\d.]+\s+(or|and)\s+\w+\s*[=≠≈<>≤≥]\s*[\d.]+/gi,

    // If-then statements
    /if\s+.+\s+(then|implies)/gi,

    // Mathematical expressions with operators
    /\w+\s*[+\-*/^=<>≠≈≤≥±∓×÷]\s*\w+/g,

    // Subscripts and superscripts (x₁, x², etc.)
    /\w+[₀₁₂₃₄₅₆₇₈₉⁰¹²³⁴⁵⁶⁷⁸⁹ⁿ]/g,

    // Scientific notation
    /\d+\.?\d*\s*[eE×]\s*10\^?[\-+]?\d+/g,

    // Fractions and ratios
    /\d+\/\d+/g,

    // Greek letters in formulas
    /[αβγδεζηθικλμνξοπρστυφχψωΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ]/,
  ];

  // Check if detected patterns are actually academic content
  const isWhitelisted = academicWhitelist.some((pattern) => pattern.test(text));

  // Common SQL injection patterns (tightened to reduce false positives)
  const sqlPatterns = [
    // Classic SQL injection (more specific - requires quotes and SQL context)
    /(\'\s*or\s*\'\d+\'\s*=\s*\'\d+)/gi, // OR '1'='1' (with quotes)
    /(\'\s*and\s*\'\d+\'\s*=\s*\'\d+)/gi, // AND '1'='1' (with quotes)

    // ✅ TIGHTENED: Only flag "1=1" if it appears with SQL-specific context
    /(\'\s*or\s+1\s*=\s*1\s*--)/gi, // ' OR 1=1 --
    /(\'\s*and\s+1\s*=\s*1\s*--)/gi, // ' AND 1=1 --

    // SQL commands (only block if suspicious context)
    /(;\s*(drop|delete|truncate)\s+(table|database))/gi,
    /(union\s+select\s+)/gi,
    /(select\s+\*\s+from\s+)/gi, // SELECT * FROM
    /(insert\s+into\s+.+\s+values\s*\()/gi,

    // SQL comment injection (with quotes)
    /(\'\s*;\s*--)/gi, // '; --
    /(\'\s+or\s+.+\s*--)/gi, // ' OR ... --

    // UNION-based injection
    /(union\s+all\s+select)/gi,
    /(union\s+select\s+null)/gi,

    // ✅ TIGHTENED: Only flag hex if it's suspicious (long chains)
    /(0x[0-9a-f]{8,})/gi, // Hex values (8+ chars - likely malicious)

    // CHAR() function with suspicious context
    /(char\s*\(\s*\d+\s*\)\s*\+)/gi, // CHAR() concatenation

    // Stacked queries with suspicious commands
    /(;\s*(drop|delete|update|insert)\s+)/gi,

    // Blind injection
    /(\bsleep\s*\(\s*\d+\s*\))/gi, // SLEEP() function
    /(\bwaitfor\s+delay)/gi, // WAITFOR DELAY
    /(\bbenchmark\s*\(\s*\d+)/gi, // BENCHMARK()
  ];

  // Check each pattern
  for (const pattern of sqlPatterns) {
    const matches = text.match(pattern);
    if (matches) {
      detectedPatterns.push(...matches);
    }
  }

  // ✅ IMPROVED: Additional heuristic checks (more specific)
  const suspiciousIndicators = [
    // Only flag if quotes are involved (key indicator of SQL injection)
    lowerText.includes("' or '") && lowerText.includes("='"),
    lowerText.includes('" or "') && lowerText.includes('="'),
    // Only flag "1=1" with SQL comment or in quotes
    (lowerText.includes("1=1") || lowerText.includes("1 = 1")) &&
      (lowerText.includes("--") || lowerText.includes("'")),
    // SELECT FROM WHERE must be together (not scattered across document)
    /select.{0,50}from.{0,50}where/i.test(lowerText),
    // UNION SELECT together
    /union.{0,20}select/i.test(lowerText),
    // SQL comment with OR/AND nearby (within 20 chars)
    lowerText.includes("--") && /(or|and).{0,20}--/i.test(lowerText),
  ];

  const suspiciousCount = suspiciousIndicators.filter(Boolean).length;

  // ✅ NEW: If content is whitelisted or has clear academic context, don't flag as attack
  if (isWhitelisted || hasAcademicContext) {
    if (detectedPatterns.length > 0 || suspiciousCount > 0) {
      logDebug("✅ Potential SQL patterns detected but WHITELISTED (academic content)", {
        textPreview: text.substring(0, 100),
        detectedPatterns: detectedPatterns.slice(0, 3),
        hasAcademicContext,
        isWhitelisted,
      });
    }
    return { isAttack: false };
  }

  // ✅ IMPROVED: Only flag as attack if patterns detected AND not academic content
  // Increased threshold from 2 to 3 for suspiciousCount - more lenient for math/science content
  if (detectedPatterns.length > 0 || suspiciousCount >= 3) {
    logWarn("⚠️ SQL INJECTION ATTEMPT DETECTED", {
      textPreview: text.substring(0, 100),
      detectedPatterns: detectedPatterns.slice(0, 5),
      suspiciousCount,
    });

    return {
      isAttack: true,
      reason:
        "SQL injection pattern detected in input. This type of content is not allowed for security reasons.",
      detectedPatterns: detectedPatterns.slice(0, 5),
    };
  }

  return { isAttack: false };
}

/**
 * ✅ SECURITY: Detect potential DDoS/abuse patterns
 * Prevents excessive API calls and resource exhaustion
 * FULLY ALLOWS: All symbols, special characters, math notation - ONLY checks for spam/abuse patterns
 */
export function detectAbusiveInput(text: string): { isAbusive: boolean; reason?: string } {
  if (!text || typeof text !== "string") {
    return { isAbusive: false };
  }

  // Check for excessively long input (>100KB characters)
  if (text.length > 100000) {
    return {
      isAbusive: true,
      reason: "Input too large (exceeds 100,000 characters). Please reduce the content size.",
    };
  }

  // Check for excessive repetition (copy-paste spam)
  const words = text.split(/\s+/);
  if (words.length > 100) {
    const uniqueWords = new Set(words.map((w) => w.toLowerCase()));
    const uniqueRatio = uniqueWords.size / words.length;

    if (uniqueRatio < 0.05) {
      // Reduced from 0.1 to 0.05 - only extreme spam
      return {
        isAbusive: true,
        reason: "Excessive repetition detected. Please provide meaningful, varied content.",
      };
    }
  }

  // REMOVED: Non-alphanumeric ratio check (was blocking legitimate math/science content)
  // ALL symbols and special characters are now fully allowed

  // DISABLED: Control character check for educational content
  // The check was too restrictive and blocked legitimate Unicode characters like:
  // - En-dash (–), em-dash (—), smart quotes (" " ' ')
  // - Mathematical symbols, scientific notation
  // - International characters and proper punctuation
  //
  // We only check for EXTREME cases of truly malicious null bytes (not formatting chars)
  // Only block if text is MOSTLY null bytes (>50% null bytes = binary malware)
  const nullBytes = text.match(/\x00/g) || [];
  const nullByteRatio = text.length > 0 ? nullBytes.length / text.length : 0;

  // Only block if more than 50% of the text is null bytes (clearly binary/malware)
  if (nullByteRatio > 0.5) {
    logWarn("⚠️ Binary content detected (excessive null bytes)", {
      nullByteCount: nullBytes.length,
      totalLength: text.length,
      ratio: nullByteRatio.toFixed(3),
    });

    return {
      isAbusive: true,
      reason: "Input contains binary data and cannot be processed as text.",
    };
  }

  return { isAbusive: false };
}
/**
 * ✅ SECURITY: Detect XSS (Cross-Site Scripting) attempts
 * Prevents malicious script injection
 */
export function detectXSSAttempt(text: string): {
  isAttack: boolean;
  reason?: string;
  detectedPatterns?: string[];
} {
  if (!text || typeof text !== "string") {
    return { isAttack: false };
  }

  const lowerText = text.toLowerCase();
  const detectedPatterns: string[] = [];

  // XSS patterns
  const xssPatterns = [
    /<script[^>]*>.*?<\/script>/gi, // <script> tags
    /<iframe[^>]*>.*?<\/iframe>/gi, // <iframe> tags
    /javascript\s*:/gi, // javascript: protocol
    /on(load|error|click|mouse\w+)\s*=/gi, // Event handlers
    /<img[^>]+src\s*=\s*["']?javascript:/gi, // Image with JS
    /<svg[^>]*on\w+\s*=/gi, // SVG with events
    /eval\s*\(/gi, // eval() function
    /expression\s*\(/gi, // CSS expression()
    /<object[^>]*>/gi, // <object> tags
    /<embed[^>]*>/gi, // <embed> tags
  ];

  for (const pattern of xssPatterns) {
    const matches = text.match(pattern);
    if (matches) {
      detectedPatterns.push(...matches);
    }
  }

  if (detectedPatterns.length > 0) {
    logWarn("⚠️ XSS ATTEMPT DETECTED", {
      textPreview: text.substring(0, 100),
      detectedPatterns: detectedPatterns.slice(0, 3),
    });

    return {
      isAttack: true,
      reason: "Script injection attempt detected in input. HTML/JavaScript code is not allowed.",
      detectedPatterns: detectedPatterns.slice(0, 3),
    };
  }

  return { isAttack: false };
}

/**
 * ✅ COMPREHENSIVE SECURITY CHECK: Combines all security validations
 * Single function to detect malware, SQL injection, XSS, DDoS, and inappropriate content
 */
export function comprehensiveSecurityCheck(text: string): SafetyCheckResult {
  // Step 1: Check for SQL injection
  const sqlCheck = detectSQLInjection(text);
  if (sqlCheck.isAttack) {
    return {
      isSafe: false,
      blocked: true,
      severity: "critical",
      reasons: ["SQL injection attempt detected"],
      message: "🚫 Security Alert: " + sqlCheck.reason,
      reason: sqlCheck.reason,
      detectedTerms: sqlCheck.detectedPatterns,
    };
  }

  // Step 2: Check for XSS attempts
  const xssCheck = detectXSSAttempt(text);
  if (xssCheck.isAttack) {
    return {
      isSafe: false,
      blocked: true,
      severity: "critical",
      reasons: ["Cross-site scripting (XSS) attempt detected"],
      message: "🚫 Security Alert: " + xssCheck.reason,
      reason: xssCheck.reason,
      detectedTerms: xssCheck.detectedPatterns,
    };
  }

  // Step 3: Check for abusive input (DDoS prevention)
  const abuseCheck = detectAbusiveInput(text);
  if (abuseCheck.isAbusive) {
    return {
      isSafe: false,
      blocked: true,
      severity: "high",
      reasons: ["Abusive input pattern detected"],
      message: "⚠️ " + abuseCheck.reason,
      reason: abuseCheck.reason,
    };
  }

  // Step 4: Check for inappropriate content (profanity, violence, etc.)
  const contentCheck = checkContentSafety(text);
  if (!contentCheck.isSafe || contentCheck.blocked) {
    return contentCheck;
  }

  // All checks passed
  return {
    isSafe: true,
    blocked: false,
    severity: "none",
    reasons: [],
  };
}

/**
 * Alias for validateTextInputSafety - used by AINoteProcessor.vue
 */
export const checkFileSafety = validateFileContentSafety;
